import React, { useCallback, useEffect, useState } from "react";
import { Button, Surface, Tabs } from "@heroui/react";
import { LoadingState } from "../components/LoadingState";
import { PortalBillingMetrics } from "../components/PortalBillingMetrics";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { showErrorToast, showSuccessToast } from "../lib/toast";

export function BillingPage() {
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [toggling, setToggling] = useState(null);

  const loadBilling = useCallback(async () => {
    const data = await apiFetch("/api/billing");
    setBilling(data);
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadBilling()
      .catch(() => {
        if (!cancelled) showErrorToast(t("billing.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loadBilling, t]);

  const toggleModule = async (moduleKey, enabled) => {
    setToggling(moduleKey);
    try {
      await apiFetch("/api/portal/modules", {
        method: "PATCH",
        body: JSON.stringify({ module_key: moduleKey, enabled })
      });
      showSuccessToast(t("billing.moduleUpdated") || "Module updated");
      await loadBilling();
    } catch {
      showErrorToast(t("billing.moduleUpdateError") || "Unable to update module");
    } finally {
      setToggling(null);
    }
  };

  if (loading) return <LoadingState label={t("common.loadingBilling")} />;

  const showMetrics =
    (billing?.free_withdrawals_total ?? 0) > 0 ||
    billing?.transaction_quota ||
    (billing?.premium_ai_unlocked && billing?.premium_ai_usage);

  const modules = billing?.modules;

  return (
    <div className="portal-withdrawals-layout">
      <Tabs
        selectedKey={activeTab}
        onSelectionChange={(key) => setActiveTab(String(key))}
        orientation="vertical"
        className="portal-status-tabs w-full max-w-[220px]"
      >
        <Tabs.ListContainer>
          <Tabs.List
            aria-label={t("billing.pageTitle")}
            className="portal-status-tabs__list w-full *:w-full *:justify-start *:px-3 *:py-2 *:text-sm *:font-medium *:data-[selected=true]:text-accent-foreground"
          >
            <Tabs.Tab id="overview">
              {t("billing.pageTitle")}
              <Tabs.Indicator className="bg-accent" />
            </Tabs.Tab>
          </Tabs.List>
        </Tabs.ListContainer>
      </Tabs>

      <div className="portal-withdrawals-layout__content">
        <Surface className="portal-page-surface">
          <h2 className="portal-panel__title">{t("billing.pageTitle")}</h2>

          <div className="portal-banner portal-banner--info">
            <p className="font-medium">{t("billing.currencyNoticeHeading")}</p>
            <p className="mt-1">{t("billing.currencyNotice")}</p>
          </div>

          {showMetrics && (
            <PortalBillingMetrics
              freeWithdrawalsRemaining={billing?.free_withdrawals_remaining ?? 0}
              freeWithdrawalsTotal={billing?.free_withdrawals_total ?? 0}
              transactionQuota={billing?.transaction_quota}
              premiumAiUsage={billing?.premium_ai_unlocked ? billing?.premium_ai_usage : null}
            />
          )}

          {billing?.message && <p className="portal-banner portal-banner--info">{billing.message}</p>}

          <div className="grid gap-2 text-sm">
            <p>
              <strong>{t("billing.provider") || "Provider"}:</strong>{" "}
              {billing?.billing_provider || t("common.dash")}
            </p>
            <p>
              <strong>{t("billing.planActive")}:</strong>{" "}
              {billing?.plan_active ? t("common.yes") || "Yes" : t("common.no") || "No"}
            </p>
            {(billing?.free_withdrawals_total ?? 0) > 0 && (
              <p>
                <strong>{t("billing.metrics.freeWithdrawals")}:</strong>{" "}
                {t("billing.metrics.freeWithdrawalsValue", {
                  remaining: billing?.free_withdrawals_remaining ?? 0,
                  total: billing?.free_withdrawals_total ?? 0
                })}
              </p>
            )}
          </div>

          {modules ? (
            <div className="mt-6 flex flex-col gap-4">
              <h3 className="text-lg font-semibold">
                {t("billing.modulesHeading") || "Compliance modules"}
              </h3>
              <p className="text-sm text-default-500">
                {t("billing.modulesHelp") ||
                  "Enable Guarantees and AI Labels for standalone storefront embeds. Payment collection for these modules is coming later."}
              </p>
              {[
                {
                  key: "guarantees",
                  title: t("nav.guarantees") || "Guarantees",
                  body: t("billing.guaranteesModuleBody") || "Legal guarantee notice and GARAN labels."
                },
                {
                  key: "ai_labels",
                  title: t("nav.aiLabels") || "AI Labels",
                  body: t("billing.aiLabelsModuleBody") || "Art. 50 AI transparency overlays."
                }
              ].map((mod) => {
                const state = modules[mod.key] || {};
                const enabled = Boolean(state.enabled);
                return (
                  <div
                    key={mod.key}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-default-200 p-4"
                  >
                    <div>
                      <p className="font-medium">{mod.title}</p>
                      <p className="text-sm text-default-500">{mod.body}</p>
                      <p className="mt-1 text-xs text-default-400">
                        {enabled
                          ? t("billing.moduleOn") || "Enabled"
                          : t("billing.moduleOff") || "Disabled"}
                      </p>
                    </div>
                    <Button
                      variant={enabled ? "secondary" : "primary"}
                      className={enabled ? "" : "portal-accent-button"}
                      isDisabled={toggling === mod.key}
                      onPress={() => toggleModule(mod.key, !enabled)}
                    >
                      {enabled
                        ? t("billing.disableModule") || "Disable"
                        : t("billing.enableModule") || "Enable"}
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : null}

          {billing?.transaction_quota && (
            <pre className="portal-pre">{JSON.stringify(billing.transaction_quota, null, 2)}</pre>
          )}
        </Surface>
      </div>
    </div>
  );
}
